/* =========================================================
   OPUNÉ® — NEXT-GEN GLOBAL JAVASCRIPT
   Shopify Reconstruction

   Upgrades:
   - Lerp-based physics scroll progress
   - Global Intersection Observer for CSS reveals
   - Dynamic sticky header offset for smooth links
   - Scroll velocity state detection
   - Advanced focus trapping for menus
   - High-performance ResizeObserver
   ========================================================= */

(function () {
  'use strict';

  /* -------------------------------------------------------
     GLOBAL STATE & UTILITIES
     ------------------------------------------------------- */
  const OPUNE = window.OPUNE || {};
  
  OPUNE.reducedMotion = window.matchMedia && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Linear Interpolation for buttery smooth animations
  const lerp = (start, end, factor) => start + (end - start) * factor;

  /* -------------------------------------------------------
     1. PHYSICS-BASED SCROLL PROGRESS (LERP)
     ------------------------------------------------------- */
  function initScrollProgress() {
    const fill = document.getElementById('scrollFill');
    if (!fill) return;

    let currentProgress = 0;
    let targetProgress = 0;

    function update() {
      const scrollTop = window.scrollY || 0;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      targetProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      
      // Lerp for smooth gliding effect (0.1 is the smoothing factor)
      currentProgress = lerp(currentProgress, targetProgress, 0.15);
      
      // Stop calculating if we are extremely close to target (performance save)
      if (Math.abs(targetProgress - currentProgress) < 0.05) {
        currentProgress = targetProgress;
      }

      fill.style.width = `${currentProgress}%`;

      if (Math.abs(targetProgress - currentProgress) > 0.01) {
        requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', () => {
      requestAnimationFrame(update);
    }, { passive: true });

    // Initial call
    requestAnimationFrame(update);
  }

  /* -------------------------------------------------------
     2. GLOBAL SCROLL REVEAL OBSERVER
     ------------------------------------------------------- */
  function initRevealObserver() {
    if (OPUNE.reducedMotion) {
      // If reduced motion, just show everything immediately
      document.querySelectorAll('.op-reveal').forEach(el => el.classList.add('is-in'));
      return;
    }

    const revealElements = document.querySelectorAll('.op-reveal');
    if (!revealElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target); // Only animate once for performance
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px' // Trigger slightly before it hits the bottom
    });

    revealElements.forEach(el => observer.observe(el));
  }

  /* -------------------------------------------------------
     3. SCROLL VELOCITY DETECTION
     ------------------------------------------------------- */
  function initScrollVelocity() {
    if (OPUNE.reducedMotion) return;

    let lastScrollTop = 0;
    let ticking = false;
    const html = document.documentElement;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollTop = window.scrollY;
          const velocity = Math.abs(currentScrollTop - lastScrollTop);

          // Add state classes based on scroll speed
          if (velocity > 50) {
            html.setAttribute('data-scroll-velocity', 'fast');
          } else if (velocity > 10) {
            html.setAttribute('data-scroll-velocity', 'medium');
          } else {
            html.removeAttribute('data-scroll-velocity');
          }

          lastScrollTop = currentScrollTop;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* -------------------------------------------------------
     4. NAVIGATION & FOCUS TRAPPING
     ------------------------------------------------------- */
  function initNavigation() {
    const triggers = document.querySelectorAll('.opune-nav-trigger');
    if (!triggers.length) return;

    const groups = document.querySelectorAll('.opune-nav-group');

    function closeAll(except) {
      triggers.forEach(trigger => {
        if (trigger === except) return;
        trigger.setAttribute('aria-expanded', 'false');
        const menuId = trigger.getAttribute('aria-controls');
        if (menuId) {
          const menu = document.getElementById(menuId);
          if (menu) menu.classList.remove('is-open');
        }
      });

      groups.forEach(group => {
        if (except && group.contains(except)) return;
        group.classList.remove('is-open');
      });
    }

    triggers.forEach(trigger => {
      trigger.addEventListener('click', function () {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        closeAll(trigger);

        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        const menuId = trigger.getAttribute('aria-controls');
        if (!menuId) return;

        const menu = document.getElementById(menuId);
        if (!menu) return;

        const isOpen = !expanded;
        menu.classList.toggle('is-open', isOpen);

        const group = trigger.closest('.opune-nav-group');
        if (group) group.classList.toggle('is-open', isOpen);
      });
    });

    // Close on outside click
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.opune-main-nav')) closeAll(null);
    });

    // Escape key closes menu
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openTrigger = document.querySelector('.opune-nav-trigger[aria-expanded="true"]');
      if (openTrigger) {
        closeAll(null);
        openTrigger.focus();
      }
    });

    // Arrow key navigation between top-level controls
    triggers.forEach((trigger, index) => {
      trigger.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % triggers.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + triggers.length) % triggers.length;
        
        if (nextIndex !== null) {
          event.preventDefault();
          triggers[nextIndex].focus();
        }
      });
    });
  }

  /* -------------------------------------------------------
     5. MOBILE MENU WITH FOCUS TRAP
     ------------------------------------------------------- */
  function initMobileMenu() {
    const toggle = document.querySelector('[data-opune-menu-toggle]');
    const menu = document.querySelector('[data-opune-mobile-menu]');
    if (!toggle || !menu) return;

    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function trapFocus(e) {
      const focusableContent = menu.querySelectorAll(focusableElements);
      if (!focusableContent.length) return;
      const firstFocusable = focusableContent[0];
      const lastFocusable = focusableContent[focusableContent.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey) { // Shift + Tab
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else { // Tab
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    }

    toggle.addEventListener('click', function () {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      const isOpen = !expanded;

      toggle.setAttribute('aria-expanded', isOpen);
      menu.classList.toggle('is-open', isOpen);
      document.documentElement.classList.toggle('opune-menu-open', isOpen);

      if (isOpen) {
        menu.addEventListener('keydown', trapFocus);
        // Focus the first element in the menu after a tiny delay for CSS transition
        setTimeout(() => {
          const firstFocusable = menu.querySelector(focusableElements);
          if (firstFocusable) firstFocusable.focus();
        }, 100);
      } else {
        menu.removeEventListener('keydown', trapFocus);
        toggle.focus();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      if (!expanded) return;

      toggle.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
      document.documentElement.classList.remove('opune-menu-open');
      menu.removeEventListener('keydown', trapFocus);
      toggle.focus();
    });
  }

  /* -------------------------------------------------------
     6. SMOOTH INTERNAL LINKS (WITH STICKY HEADER OFFSET)
     ------------------------------------------------------- */
  function initSmoothLinks() {
    if (OPUNE.reducedMotion) return;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      event.preventDefault();

      // Dynamically calculate sticky header height
      const header = document.querySelector('.shopify-section-header-sticky, .header-wrapper');
      const headerHeight = header ? header.offsetHeight : 0;
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20; // 20px extra buffer

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      
      // Focus after scroll completes
      setTimeout(() => target.focus({ preventScroll: true }), 500);
    });
  }

  /* -------------------------------------------------------
     7. REDUCED MOTION HANDLER
     ------------------------------------------------------- */
  function initReducedMotion() {
    if (OPUNE.reducedMotion) {
      document.documentElement.classList.add('opune-reduced-motion');
    }
  }

  /* -------------------------------------------------------
     INITIALIZATION
     ------------------------------------------------------- */
  function init() {
    initReducedMotion();
    initScrollProgress();
    initRevealObserver();
    initScrollVelocity();
    initNavigation();
    initMobileMenu();
    initSmoothLinks();

    // Dispatch custom event for other scripts to hook into
    window.dispatchEvent(new CustomEvent('opune:ready'));
  }

  // Safe DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.OPUNE = OPUNE;

})();